/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {DomElementSchemaRegistry} from '@angular/compiler';
import {APP_ID, Inject, Injectable, NgZone, RenderComponentType, Renderer, Renderer2, RendererFactory2, RendererStyleFlags2, RendererType2, RootRenderer, ViewEncapsulation, ɵstringify as stringify} from '@angular/core';
import {DOCUMENT, ɵNAMESPACE_URIS as NAMESPACE_URIS, ɵSharedStylesHost as SharedStylesHost, ɵflattenStyles as flattenStyles, ɵgetDOM as getDOM, ɵshimContentAttribute as shimContentAttribute, ɵshimHostAttribute as shimHostAttribute} from '@angular/platform-browser';

const /** @type {?} */ EMPTY_ARRAY: any[] = [];
export class ServerRendererFactory2 implements RendererFactory2 {
private rendererByCompId = new Map<string, Renderer2>();
private defaultRenderer: Renderer2;
private schema = new DomElementSchemaRegistry();
/**
 * @param {?} ngZone
 * @param {?} document
 * @param {?} sharedStylesHost
 */
constructor(
private ngZone: NgZone,
private document: any,
private sharedStylesHost: SharedStylesHost) {
    this.defaultRenderer = new DefaultServerRenderer2(document, ngZone, this.schema);
  };
/**
 * @param {?} element
 * @param {?} type
 * @return {?}
 */
createRenderer(element: any, type: RendererType2|null): Renderer2 {
    if (!element || !type) {
      return this.defaultRenderer;
    }
    switch (type.encapsulation) {
      case ViewEncapsulation.Native:
      case ViewEncapsulation.Emulated: {
        let /** @type {?} */ renderer = this.rendererByCompId.get(type.id);
        if (!renderer) {
          renderer = new EmulatedEncapsulationServerRenderer2(
              this.document, this.ngZone, this.sharedStylesHost, this.schema, type);
          this.rendererByCompId.set(type.id, renderer);
        }
        ( /** @type {?} */((<EmulatedEncapsulationServerRenderer2>renderer))).applyToHost(element);
        return renderer;
      }
      case ViewEncapsulation.Native:
        throw new Error('Native encapsulation is not supported on the server!');
      default: {
        if (!this.rendererByCompId.has(type.id)) {
          const /** @type {?} */ styles = flattenStyles(type.id, type.styles, []);
          this.sharedStylesHost.addStyles(styles);
          this.rendererByCompId.set(type.id, this.defaultRenderer);
        }
        return this.defaultRenderer;
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: NgZone, },
{type: undefined, decorators: [{ type: Inject, args: [DOCUMENT, ] }, ]},
{type: SharedStylesHost, },
];
}

function ServerRendererFactory2_tsickle_Closure_declarations() {
/** @type {?} */
ServerRendererFactory2.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ServerRendererFactory2.ctorParameters;
/** @type {?} */
ServerRendererFactory2.prototype.rendererByCompId;
/** @type {?} */
ServerRendererFactory2.prototype.defaultRenderer;
/** @type {?} */
ServerRendererFactory2.prototype.schema;
/** @type {?} */
ServerRendererFactory2.prototype.ngZone;
/** @type {?} */
ServerRendererFactory2.prototype.document;
/** @type {?} */
ServerRendererFactory2.prototype.sharedStylesHost;
}

class DefaultServerRenderer2 implements Renderer2 {
  data: {[key: string]: any} = Object.create(null);
/**
 * @param {?} document
 * @param {?} ngZone
 * @param {?} schema
 */
constructor(
private document: any,
private ngZone: NgZone,
private schema: DomElementSchemaRegistry) {}
/**
 * @return {?}
 */
destroy(): void {}

  destroyNode: null;
/**
 * @param {?} name
 * @param {?=} namespace
 * @param {?=} debugInfo
 * @return {?}
 */
createElement(name: string, namespace?: string, debugInfo?: any): any {
    if (namespace) {
      return getDOM().createElementNS(NAMESPACE_URIS[namespace], name);
    }

    return getDOM().createElement(name);
  }
/**
 * @param {?} value
 * @param {?=} debugInfo
 * @return {?}
 */
createComment(value: string, debugInfo?: any): any { return getDOM().createComment(value); }
/**
 * @param {?} value
 * @param {?=} debugInfo
 * @return {?}
 */
createText(value: string, debugInfo?: any): any { return getDOM().createTextNode(value); }
/**
 * @param {?} parent
 * @param {?} newChild
 * @return {?}
 */
appendChild(parent: any, newChild: any): void { getDOM().appendChild(parent, newChild); }
/**
 * @param {?} parent
 * @param {?} newChild
 * @param {?} refChild
 * @return {?}
 */
insertBefore(parent: any, newChild: any, refChild: any): void {
    if (parent) {
      getDOM().insertBefore(parent, refChild, newChild);
    }
  }
/**
 * @param {?} parent
 * @param {?} oldChild
 * @return {?}
 */
removeChild(parent: any, oldChild: any): void {
    if (parent) {
      getDOM().removeChild(parent, oldChild);
    }
  }
/**
 * @param {?} selectorOrNode
 * @param {?=} debugInfo
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any, debugInfo?: any): any {
    let /** @type {?} */ el: any;
    if (typeof selectorOrNode === 'string') {
      el = getDOM().querySelector(this.document, selectorOrNode);
      if (!el) {
        throw new Error(`The selector "${selectorOrNode}" did not match any elements`);
      }
    } else {
      el = selectorOrNode;
    }
    getDOM().clearNodes(el);
    return el;
  }
/**
 * @param {?} node
 * @return {?}
 */
parentNode(node: any): any { return getDOM().parentElement(node); }
/**
 * @param {?} node
 * @return {?}
 */
nextSibling(node: any): any { return getDOM().nextSibling(node); }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @param {?=} namespace
 * @return {?}
 */
setAttribute(el: any, name: string, value: string, namespace?: string): void {
    if (namespace) {
      getDOM().setAttributeNS(el, NAMESPACE_URIS[namespace], namespace + ':' + name, value);
    } else {
      getDOM().setAttribute(el, name, value);
    }
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
removeAttribute(el: any, name: string, namespace?: string): void {
    if (namespace) {
      getDOM().removeAttributeNS(el, NAMESPACE_URIS[namespace], name);
    } else {
      getDOM().removeAttribute(el, name);
    }
  }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
addClass(el: any, name: string): void { getDOM().addClass(el, name); }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
removeClass(el: any, name: string): void { getDOM().removeClass(el, name); }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} value
 * @param {?} flags
 * @return {?}
 */
setStyle(el: any, style: string, value: any, flags: RendererStyleFlags2): void {
    getDOM().setStyle(el, style, value);
  }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} flags
 * @return {?}
 */
removeStyle(el: any, style: string, flags: RendererStyleFlags2): void {
    getDOM().removeStyle(el, style);
  }
/**
 * @param {?} tagName
 * @param {?} propertyName
 * @return {?}
 */
private _isSafeToReflectProperty(tagName: string, propertyName: string): boolean {
    return this.schema.securityContext(tagName, propertyName, true) ===
        this.schema.securityContext(tagName, propertyName, false);
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any): void {
    checkNoSyntheticProp(name, 'property');
    getDOM().setProperty(el, name, value);
    // Mirror property values for known HTML element properties in the attributes.
    const /** @type {?} */ tagName = ( /** @type {?} */((el.tagName as string))).toLowerCase();
    if (value != null && (typeof value === 'number' || typeof value == 'string') &&
        this.schema.hasElement(tagName, EMPTY_ARRAY) &&
        this.schema.hasProperty(tagName, name, EMPTY_ARRAY) &&
        this._isSafeToReflectProperty(tagName, name)) {
      this.setAttribute(el, name, value.toString());
    }
  }
/**
 * @param {?} node
 * @param {?} value
 * @return {?}
 */
setValue(node: any, value: string): void { getDOM().setText(node, value); }
/**
 * @param {?} target
 * @param {?} eventName
 * @param {?} callback
 * @return {?}
 */
listen(
      target: 'document'|'window'|'body'|any, eventName: string,
      callback: (event: any) => boolean): () => void {
    // Note: We are not using the EventsPlugin here as this is not needed
    // to run our tests.
    checkNoSyntheticProp(eventName, 'listener');
    const /** @type {?} */ el =
        typeof target === 'string' ? getDOM().getGlobalEventTarget(this.document, target) : target;
    const /** @type {?} */ outsideHandler = (event: any) => this.ngZone.runGuarded(() => callback(event));
    return this.ngZone.runOutsideAngular(() => getDOM().onAndCancel(el, eventName, outsideHandler));
  }
}

function DefaultServerRenderer2_tsickle_Closure_declarations() {
/** @type {?} */
DefaultServerRenderer2.prototype.data;
/** @type {?} */
DefaultServerRenderer2.prototype.destroyNode;
/** @type {?} */
DefaultServerRenderer2.prototype.document;
/** @type {?} */
DefaultServerRenderer2.prototype.ngZone;
/** @type {?} */
DefaultServerRenderer2.prototype.schema;
}


const /** @type {?} */ AT_CHARCODE = '@'.charCodeAt(0);
/**
 * @param {?} name
 * @param {?} nameKind
 * @return {?}
 */
function checkNoSyntheticProp(name: string, nameKind: string) {
  if (name.charCodeAt(0) === AT_CHARCODE) {
    throw new Error(
        `Found the synthetic ${nameKind} ${name}. Please include either "BrowserAnimationsModule" or "NoopAnimationsModule" in your application.`);
  }
}
class EmulatedEncapsulationServerRenderer2 extends DefaultServerRenderer2 {
private contentAttr: string;
private hostAttr: string;
/**
 * @param {?} document
 * @param {?} ngZone
 * @param {?} sharedStylesHost
 * @param {?} schema
 * @param {?} component
 */
constructor(
      document: any, ngZone: NgZone, sharedStylesHost: SharedStylesHost,
      schema: DomElementSchemaRegistry,
private component: RendererType2) {
    super(document, ngZone, schema);
    const styles = flattenStyles(component.id, component.styles, []);
    sharedStylesHost.addStyles(styles);

    this.contentAttr = shimContentAttribute(component.id);
    this.hostAttr = shimHostAttribute(component.id);
  }
/**
 * @param {?} element
 * @return {?}
 */
applyToHost(element: any) { super.setAttribute(element, this.hostAttr, ''); }
/**
 * @param {?} parent
 * @param {?} name
 * @return {?}
 */
createElement(parent: any, name: string): Element {
    const /** @type {?} */ el = super.createElement(parent, name);
    super.setAttribute(el, this.contentAttr, '');
    return el;
  }
}

function EmulatedEncapsulationServerRenderer2_tsickle_Closure_declarations() {
/** @type {?} */
EmulatedEncapsulationServerRenderer2.prototype.contentAttr;
/** @type {?} */
EmulatedEncapsulationServerRenderer2.prototype.hostAttr;
/** @type {?} */
EmulatedEncapsulationServerRenderer2.prototype.component;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
